import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { AxiosInstance } from 'axios';
import { ComponentConfig } from '@measured/puck';
import { MarginProps, marginFields } from '../../../puck/reusable-props/margin';
import {
  PaddingProps,
  paddingFields,
} from '../../../puck/reusable-props/padding';
import CustomTooltip from '../../../../components/common/tooltip';
import React from 'react';
import PaypalButton, { PayPalColor } from './PaypalButton';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../../puck/reusable-props/widthAndHeight';
import { Switch, Tooltip } from '@mui/material';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import { InfoIcon } from 'lucide-react';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';

export interface PayPalButtonComponentProps
  extends ReduxSelectorProps,
    ReduxDispatchProps {
  baseService?: AxiosInstance;
}
export type PayPalButtonProps = {
  background: {
    color: string;
    isInherit: boolean;
  };
  buttonCustomization: WidthAndHeightSubProps;
  headerText: string;
  showOnlyButton: boolean;
  buttonColor: PayPalColor;
  justifyContent: string;
  border: IBorderValueProps;
  TermsAndConCheck: { isTermsAndConChecked: boolean; errorMsg: string };
} & MarginProps &
  PaddingProps;

export const PayPal = ({
  useAppDispatch,
  useAppSelector,
}: PayPalButtonComponentProps): ComponentConfig<PayPalButtonProps> => {
  return {
    label: (
      <CustomTooltip title="PayPalButton">
        <span>PayPal</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      TermsAndConCheck: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="space-y-4">
              {/* Toggle for isTermsAndConChecked */}
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold">
                  Require Terms & Conditions
                </p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title="Toggle to require terms and conditions acceptance"
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-4 w-4" />
                  </Tooltip>
                  <Switch
                    onChange={e => {
                      onChange({
                        ...value,
                        isTermsAndConChecked: e.target.checked,
                      });
                    }}
                    checked={value?.isTermsAndConChecked}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>

              {/* Input for errorMsg */}
              {value?.isTermsAndConChecked && (
                <div className="flex flex-col">
                  <label className="text-sm font-semibold mb-1">
                    Error Message for Terms & Conditions
                  </label>
                  <input
                    type="text"
                    value={value?.errorMsg}
                    onChange={e =>
                      onChange({
                        ...value,
                        errorMsg: e.target.value,
                      })
                    }
                    placeholder="Enter error message"
                    className="border border-gray-300 rounded-md p-2 text-sm"
                  />
                </div>
              )}
            </div>
          );
        },
      },
      headerText: {
        ...QuillTextEditor('Header Text').content,
      },
      showOnlyButton: {
        type: 'custom',
        render: ({ onChange, value }) => {
          return (
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show only Button</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggle this switch to show only the Button'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  onChange={e => {
                    onChange(e.target.checked);
                  }}
                  checked={value}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
          );
        },
      },
      buttonColor: {
        type: 'select',
        label: 'Button Color',
        options: [
          { label: 'Gold', value: 'gold' },
          { label: 'Blue', value: 'blue' },
          { label: 'Silver', value: 'silver' },
          { label: 'White', value: 'white' },
          { label: 'Black', value: 'black' },
        ],
      },
      justifyContent: {
        type: 'select',
        label: 'Button Alignment',
        options: [
          { label: 'center', value: 'm-auto' },
          { label: 'left', value: '' },
          { label: 'right', value: 'ml-auto' },
        ],
      },
      buttonCustomization: {
        type: 'custom',
        render: ({ value, onChange }) => {
          return (
            <WidthAndHeight
              value={value}
              onChange={val => {
                onChange({
                  ...value,
                  ...val,
                });
              }}
              title={'Size Customization'}
            />
          );
        },
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },
    defaultProps: {
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingLeft: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingRight: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
        paddingTop: {
          desktop: '4',
          mobile: '4',
          tablet: '4',
        },
      },
      ...borderProps,
      buttonCustomization: {
        height: {
          desktop: '48',
          mobile: '48',
          tablet: '48',
        },
        maxWidth: {
          desktop: '160',
          mobile: '160',
          tablet: '160',
        },
        deviceType: 'desktop',
      },
      TermsAndConCheck: {
        isTermsAndConChecked: false,
        errorMsg: '*Please accept the terms and conditions to proceed.',
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      justifyContent: 'm-auto',
      showOnlyButton: false,
      buttonColor: 'gold',
      headerText:
        '<p style="line-height: 1.5;" class="ql-align-center"><span style="font-size: 16px;"></span></p>',
    },
    render: ({
      margin,
      editMode,
      buttonCustomization,
      justifyContent,
      border,
      buttonColor,
      headerText,
      showOnlyButton,
      padding,
      TermsAndConCheck,
      background,
    }) => {
      return (
        <PaypalButton
          useAppSelector={useAppSelector}
          useAppDispatch={useAppDispatch}
          buttonCustomization={buttonCustomization}
          justifyContent={justifyContent}
          border={border}
          buttonColor={buttonColor}
          headerText={headerText}
          showOnlyButton={showOnlyButton}
          margin={margin}
          padding={padding}
          TermsAndConCheck={TermsAndConCheck}
          background={background}
          editMode={editMode || false}
        />
      );
    },
  };
};
